import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { productAPI } from '../../utils/api';
import { adminAPI } from '../../utils/adminAPI';
import { Plus, Edit, Trash2, Search, Wrench } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fitmentModalOpen, setFitmentModalOpen] = useState(false);
  const [fitmentTarget, setFitmentTarget] = useState(null);
  const [fitmentsDraft, setFitmentsDraft] = useState([]);
  const [savingFitment, setSavingFitment] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await productAPI.getProducts({ limit: 100 });
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: 'Error',
        description: 'Failed to load products',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await adminAPI.deleteProduct(productId);
      toast({
        title: 'Success',
        description: 'Product deleted successfully',
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive',
      });
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products [FITMENT-TEST-v2]</h1>
          <p className="text-gray-600">Manage your product inventory</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/admin/products/create')}>
          <Plus className="mr-2 h-4 w-4" />
          Add Product
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p>Loading products...</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4">Image</th>
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Brand</th>
                    <th className="text-left py-3 px-4">Price</th>
                    <th className="text-left py-3 px-4">Stock</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Fitment</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      </td>
                      <td className="py-3 px-4 font-medium">{product.name}</td>
                      <td className="py-3 px-4">{product.brand}</td>
                      <td className="py-3 px-4">£{product.price.toFixed(2)}</td>
                      <td className="py-3 px-4">{product.stock_quantity}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          product.in_stock
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.in_stock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {Array.isArray(product.fitment) && product.fitment.length > 0 ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {product.fitment.length} entries
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">None</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFitmentTarget(product);
                              setFitmentsDraft(product.fitment || []);
                              setFitmentModalOpen(true);
                            }}
                            title="Manage Fitment"
                          >
                            <Wrench className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(product.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fitment Management Modal */}
      <Dialog open={fitmentModalOpen} onOpenChange={setFitmentModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Fitment — {fitmentTarget?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-yellow-50 text-yellow-900 p-3 rounded text-sm">
              Add exact configurations that this product fits. All fields required per entry.
            </div>

            <div className="space-y-3">
              {fitmentsDraft.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input className="border rounded px-2 py-1 w-20" value={f.region} onChange={e=>{
                    const next=[...fitmentsDraft]; next[idx]={...next[idx], region:e.target.value}; setFitmentsDraft(next);
                  }} placeholder="GCC" />
                  <input className="border rounded px-2 py-1 w-28" value={f.make} onChange={e=>{
                    const next=[...fitmentsDraft]; next[idx]={...next[idx], make:e.target.value.toUpperCase()}; setFitmentsDraft(next);
                  }} placeholder="TOYOTA" />
                  <input className="border rounded px-2 py-1 w-28" value={f.model} onChange={e=>{
                    const next=[...fitmentsDraft]; next[idx]={...next[idx], model:e.target.value}; setFitmentsDraft(next);
                  }} placeholder="Camry" />
                  <input className="border rounded px-2 py-1 w-20" type="number" value={f.year} onChange={e=>{
                    const next=[...fitmentsDraft]; next[idx]={...next[idx], year:parseInt(e.target.value||0)}; setFitmentsDraft(next);
                  }} placeholder="2021" />
                  <input className="border rounded px-2 py-1 w-24" value={f.trim} onChange={e=>{
                    const next=[...fitmentsDraft]; next[idx]={...next[idx], trim:e.target.value}; setFitmentsDraft(next);
                  }} placeholder="SE" />
                  <input className="border rounded px-2 py-1 w-24" value={f.engine} onChange={e=>{
                    const next=[...fitmentsDraft]; next[idx]={...next[idx], engine:e.target.value}; setFitmentsDraft(next);
                  }} placeholder="2.5L" />
                  <Button variant="ghost" size="sm" onClick={()=>{
                    const next = fitmentsDraft.filter((_,i)=>i!==idx); setFitmentsDraft(next);
                  }}>Remove</Button>
                </div>
              ))}

              <div>
                <Button variant="outline" size="sm" onClick={()=>{
                  setFitmentsDraft([...(fitmentsDraft||[]), { region:'GCC', make:'', model:'', year:2021, trim:'', engine:'' }]);
                }}>Add Fitment</Button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={()=>setFitmentModalOpen(false)}>Cancel</Button>
              <Button disabled={savingFitment} onClick={async()=>{
                if(!fitmentTarget) return;
                setSavingFitment(true);
                try {
                  await adminAPI.updateProduct(fitmentTarget.id, { fitment: fitmentsDraft });
                  setFitmentModalOpen(false);
                  setFitmentTarget(null);
                  setFitmentsDraft([]);
                  await fetchProducts();
                  toast({ title: 'Success', description: 'Fitment saved' });
                } catch (e) {
                  toast({ title: 'Error', description: 'Failed to save fitment', variant: 'destructive' });
                } finally {
                  setSavingFitment(false);
                }
              }}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
